import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Table, Spin } from "antd";
import styles from "./index.module.less";
import banner2 from "@/assets/img/banner2.png";
import { findValueByIdAndKey } from "@/utils";
import {
  getCaseList,
  getDictIdByCode,
  getDictListById,
  getKsDict,
} from "@/services/history";

// 根据年份计算第几届
const getEditionText = (yearStr) => {
  if (!yearStr) return "";
  const year = parseInt(yearStr, 10);
  if (isNaN(year) || year < 2018) return "";
  
  const num = year - 2018 + 1;
  const chineseNums = ["", "一", "二", "三", "四", "五", "六", "七", "八", "九", "十"];
  
  if (num <= 10) return `第${chineseNums[num]}届`;
  if (num < 20) return `第十${chineseNums[num % 10]}届`;
  return `第${chineseNums[Math.floor(num / 10)]}十${chineseNums[num % 10]}届`;
};

const AwardResult = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const goDetail = (id) => {
    navigate(`/history/detail?id=${id}`);
  };

  // 数据状态
  const [historyList, setHistoryList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // 字典状态
  const [alList, setAlList] = useState([]); // 案例类型字典
  const [yyList, setYyList] = useState([]); // 医院字典
  const [ksList, setKsList] = useState([]); // 科室字典

  // 年份参数
  const queryParams = new URLSearchParams(location.search);
  const yearParam = queryParams.get("year");

  // 表格列定义
  const columns = [
    {
      title: "序号",
      dataIndex: "index",
      key: "index",
      width: 80,
      align: "center",
      render: (text, record, index) => index + 1,
    },
    {
      title: "案例题目",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <span
          className={styles["case-link"]}
          onClick={() => goDetail(record.id)}
        >
          {text}
        </span>
      ),
    },
    {
      title: "第一作者",
      key: "author",
      width: 100,
      render: (_, record) => {
        const textArray = [
          record?.userName || "",
          record?.firstName || "",
          record?.secondName || "",
        ].filter((item) => item);
        return textArray.join("、") || "--";
      },
    },
    {
      title: "案例类型",
      key: "caseType",
      width: 100,
      render: (_, record) => {
        return (
          findValueByIdAndKey(alList, record.caseType, "itemText", "id") ||
          record.anliType ||
          "--"
        );
      },
    },
    {
      title: "医院",
      key: "hospital",
      width: 200,
      render: (_, record) => {
        return (
          findValueByIdAndKey(yyList, record.institution, "itemText", "id") ||
          record.hospital ||
          "--"
        );
      },
    },
    {
      title: "科室",
      key: "dept",
      width: 150,
      render: (_, record) => {
        const value = record?.dept;
        if (!value) {
          return record.keshi || "--";
        }
        return (
          value
            .split(",")
            .map((item) => findValueByIdAndKey(ksList, item, "title", "key"))
            .filter((item) => item)
            .join("、") || "--"
        );
      },
    },
  ];

  // 加载字典数据
  const loadDictData = async () => {
    console.info("[AwardResult] 开始加载字典数据...");
    try {
      // 1. 案例类型字典 (al)
      const alResList = await getDictIdByCode({ code: "al" });
      const alId = alResList?.result?.records?.[0]?.id || "";
      if (alId) {
        const alItemList = await getDictListById({
          dictId: alId,
          pageSize: -1,
        });
        setAlList(alItemList?.result?.records || []);
      }

      // 2. 医院字典 (yy)
      const yyResList = await getDictIdByCode({ code: "yy" });
      const yyId = yyResList?.result?.records?.[0]?.id || "";
      if (yyId) {
        const yyItemList = await getDictListById({
          dictId: yyId,
          pageSize: -1,
        });
        setYyList(yyItemList?.result?.records || []);
      }

      // 3. 科室字典 (ks)
      const ksRes = await getKsDict();
      if (ksRes.success) {
        setKsList(ksRes.result || []);
      }

      console.info("[AwardResult] 字典数据加载完成");
    } catch (error) {
      console.error("[AwardResult] 字典数据加载失败:", error);
    }
  };

  // 初始化加载字典
  useEffect(() => {
    loadDictData();
  }, []);

  // 根据 URL 的年份参数获取案例数据
  useEffect(() => {
    const fetchCases = async () => {
      console.info(`[AwardResult] 准备获取获奖案例，查询年份: ${yearParam || "全部"}`);
      setIsLoading(true);
      try {
        const res = await getCaseList({
          belongYear: yearParam || "",
          isAward: "1", // 仅查询获奖案例
          pageNo: 1,
          pageSize: 1000, // 足够大以获取当年的所有数据进行前端分组
          px: "1",
          onlineStatus: "2",
        });
        if (res.code === 200) {
          console.info(`[AwardResult] 成功获取案例数据，共 ${res.result.records.length} 条`);
          setHistoryList(res.result.records || []);
        }
      } catch (error) {
        console.error("[AwardResult] 获取案例数据失败:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCases();
  }, [yearParam]);

  // 按案例类型对数据进行分组
  const groupedCases = useMemo(() => {
    if (!historyList || historyList.length === 0) return {};

    const groups = {};

    historyList.forEach((record) => {
      const typeText =
        findValueByIdAndKey(alList, record.caseType, "itemText", "id") ||
        record.anliType ||
        "其他案例";

      if (!groups[typeText]) {
        groups[typeText] = [];
      }
      groups[typeText].push(record);
    });

    console.info(`[AwardResult] 案例数据分组完成，共分为 ${Object.keys(groups).length} 组`);
    return groups;
  }, [historyList, alList]);

  return (
    <div className={styles["award-result"]}>
      <img src={banner2} alt="banner" className={styles["img-fluid"]} />

      <div className={styles["award-result-content"]}>
        {/* 按分类展示获奖案例表格 */}
        <div className={styles["award-result-content-right"]}>
          <div className={styles["right-title"]}>{getEditionText(yearParam)}案例大赛获奖结果</div>
          <div className={styles["right-title-desc"]}>
            UpToDate案例大赛在中国临床案例成果数据库的独立主持下，对所有决赛案例进行了严谨专业的盲审评估。经过多轮严格评审，以下选手荣获奖项！
          </div>

          <Spin spinning={isLoading}>
            {Object.keys(groupedCases).length > 0 ? (
              Object.keys(groupedCases).map((groupName) => (
                <div key={groupName} className={styles["group-container"]}>
                  <div className={styles["group-title"]}>{groupName}</div>
                  <Table
                    columns={columns}
                    dataSource={groupedCases[groupName]}
                    rowKey="id"
                    pagination={false}
                    bordered
                    size="middle"
                  />
                </div>
              ))
            ) : (
              <div className={styles["no-data"]}>暂无相关年份的获奖数据</div>
            )}
          </Spin>
        </div>
      </div>
    </div>
  );
};

export default AwardResult;
